/**
 * EvaluationCard — full breakdown of a scorecard (pre-rec or immersion).
 *
 * Renders:
 *   • Header: type / evaluator / created_at
 *   • Decision badge (Recrutement possible / Recommandé / Refus / etc.)
 *   • Score percentage with a coloured bar
 *   • General comment in italic
 *   • Detailed ratings grouped by block (Expression & énergie, …) with
 *     a coloured chip per note (0–5) and the optional per-criterion
 *     comment. Built so the admin / référent can audit every detail of
 *     the evaluation, not just the headline score.
 *   • Red flags list (if any)
 */
import { AlertTriangle } from "lucide-react";

import { formatDateTime } from "../../lib/format";


function noteColor(note) {
    if (note >= 4) return { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" };
    if (note === 3) return { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" };
    if (note > 0) return { bg: "bg-red-50", text: "text-red-700", border: "border-red-200" };
    return { bg: "bg-slate-100", text: "text-slate-500", border: "border-slate-200" };
}

function decisionColor(decision) {
    const d = (decision || "").toLowerCase();
    if (d.includes("recommand") || d.includes("validé") || d === "recrutement possible") {
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
    }
    if (d.includes("refus") || d.includes("rejet")) {
        return "bg-red-50 text-red-700 border-red-200";
    }
    if (d.includes("doute") || d.includes("réserve") || d.includes("à challenger")) {
        return "bg-amber-50 text-amber-700 border-amber-200";
    }
    return "bg-slate-100 text-slate-600 border-slate-200";
}

function percentageBar(pct) {
    // Map a numeric percentage to a gradient bar color
    if (pct >= 75) return "bg-emerald-500";
    if (pct >= 60) return "bg-amber-500";
    if (pct >= 40) return "bg-orange-500";
    return "bg-red-500";
}


export default function EvaluationCard({ scorecard, criteriaMap }) {
    const s = scorecard;
    const ratings = s.ratings || [];
    // Resolve criteria to readable labels. Unknown ids fall through with
    // their id displayed — better than hiding the data outright.
    const enriched = ratings.map((r) => {
        const meta = criteriaMap?.[r.criterion_id] || {};
        return {
            ...r,
            label: meta.label || r.criterion_id,
            block: meta.block || "Autres",
            weight: meta.weight,
        };
    });
    // Group by block while preserving the order they appear in the static
    // criteria list (so blocs always come in the same logical order)
    const blocks = {};
    enriched.forEach((r) => {
        if (!blocks[r.block]) blocks[r.block] = [];
        blocks[r.block].push(r);
    });

    return (
        <div className="p-5 rounded-2xl bg-white border border-shariti-gray" data-testid={`evaluation-card-${s.id}`}>
            {/* Header row */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                        {s.type === "prerec" ? "Pré-recrutement" : "Immersion terrain"}
                        {s.evaluator_name ? ` · ${s.evaluator_name}` : ""}
                    </div>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                        <span className="text-xl font-black text-shariti-black">
                            {(s.percentage || 0).toFixed(1)}%
                        </span>
                        {s.decision && (
                            <span className={`inline-flex items-center text-[11px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border ${decisionColor(s.decision)}`}>
                                {s.decision}
                            </span>
                        )}
                    </div>
                </div>
                <span className="text-[11px] font-bold text-slate-400 whitespace-nowrap">
                    {formatDateTime(s.created_at)}
                </span>
            </div>

            {/* Coloured progress bar */}
            <div className="mt-3 h-1.5 w-full rounded-full bg-shariti-cream overflow-hidden">
                <div
                    className={`h-full ${percentageBar(s.percentage || 0)} transition-[width]`}
                    style={{ width: `${Math.min(100, Math.max(0, s.percentage || 0))}%` }}
                />
            </div>

            {/* General comment */}
            {s.general_comment && (
                <div className="mt-4 p-3 rounded-xl bg-shariti-cream border border-shariti-gray">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">
                        Commentaire général
                    </div>
                    <p className="text-sm font-medium text-slate-700 italic leading-relaxed">
                        "{s.general_comment}"
                    </p>
                </div>
            )}

            {/* Per-criterion notes, grouped by block */}
            {ratings.length > 0 && (
                <div className="mt-4 space-y-4">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                        Détail des notes ({ratings.length} critère{ratings.length > 1 ? "s" : ""})
                    </div>
                    {Object.entries(blocks).map(([blockName, items]) => (
                        <div key={blockName}>
                            <div className="text-[11px] font-extrabold uppercase tracking-widest text-shariti-orange mb-2">
                                {blockName}
                            </div>
                            <div className="space-y-2">
                                {items.map((r) => {
                                    const col = noteColor(r.note);
                                    return (
                                        <div key={r.criterion_id} className="flex items-start gap-3" data-testid={`evaluation-rating-${r.criterion_id}`}>
                                            <div className={`shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-lg border ${col.bg} ${col.border} ${col.text} text-sm font-extrabold`}>
                                                {r.note}/5
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-bold text-shariti-black">
                                                    {r.label}
                                                    {r.weight && (
                                                        <span className="ml-2 text-[10px] font-bold text-slate-400 uppercase">
                                                            ×{r.weight}
                                                        </span>
                                                    )}
                                                </div>
                                                {r.comment && (
                                                    <p className="mt-1 text-xs font-medium text-slate-600 italic">
                                                        "{r.comment}"
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Red flags */}
            {s.red_flags?.length > 0 && (
                <div className="mt-4 p-3 rounded-xl bg-red-50 border border-red-200">
                    <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-shariti-red mb-2">
                        <AlertTriangle className="h-3 w-3" />
                        Points de vigilance ({s.red_flags.length})
                    </div>
                    <ul className="space-y-1">
                        {s.red_flags.map((rf, i) => (
                            <li key={i} className="text-xs font-medium text-red-700">• {rf}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
